import { supabase } from '@/integrations/supabase/client';
import { debugLogger } from '@/utils/debugLogger';

/**
 * AdminService
 * 
 * Serviço centralizado para verificação de permissões administrativas.
 * Utiliza a tabela user_roles e a função RPC is_admin_new() do Supabase.
 * 
 * IMPORTANTE: Este serviço centraliza verificações de roles de admin/moderador
 * utilizando a tabela user_roles ao invés de joia_level (descontinuado).
 */

interface RoleCache {
  isAdmin: boolean;
  isModerator: boolean;
  timestamp: number;
}

export class AdminService {
  private static roleCache = new Map<string, RoleCache>();
  private static CACHE_DURATION = 30 * 1000; // 30 segundos (reduzido de 5 minutos para segurança)
  
  /**
   * Verifica se o usuário é admin
   * @param userId - ID do usuário (opcional, usa auth.uid() se não fornecido)
   * @returns Promise<boolean> - true se é admin
   */
  static async isAdmin(userId?: string): Promise<boolean> {
    try {
      // Se userId não fornecido, usar o usuário autenticado
      const userIdToCheck = userId || (await supabase.auth.getUser()).data.user?.id;
      
      if (!userIdToCheck) {
        debugLogger.debug('[AdminService] No user ID provided or authenticated');
        return false;
      }
      
      // Verificar cache primeiro
      const cached = this.roleCache.get(userIdToCheck);
      if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
        debugLogger.debug('[AdminService] Using cached admin status:', {
          userId: userIdToCheck,
          isAdmin: cached.isAdmin
        });
        return cached.isAdmin;
      }
      
      // Chamar RPC function para verificar role
      const { data, error } = await supabase.rpc('is_admin_new', { 
        _user_id: userIdToCheck 
      });
      
      if (error) {
        debugLogger.error('[AdminService] Failed to check admin status:', error);
        return false;
      }
      
      const isAdmin = data || false;
      
      // Atualizar cache (verificar moderador também para otimizar)
      const isModerator = await this.hasRole(userIdToCheck, 'moderator');
      this.roleCache.set(userIdToCheck, { 
        isAdmin, 
        isModerator,
        timestamp: Date.now() 
      });
      
      debugLogger.debug('[AdminService] Admin status checked:', {
        userId: userIdToCheck,
        isAdmin,
        isModerator
      });
      
      return isAdmin;
    } catch (error) {
      debugLogger.error('[AdminService] Error in isAdmin:', error);
      return false;
    }
  }
  
  /**
   * Verifica se o usuário tem uma role específica
   * @param userId - ID do usuário
   * @param role - Role a verificar ('admin' | 'moderator' | 'user')
   * @returns Promise<boolean> - true se tem a role
   */
  static async hasRole(userId: string, role: 'admin' | 'moderator' | 'user'): Promise<boolean> {
    try {
      const { data, error } = await supabase.rpc('has_role', {
        _user_id: userId,
        _role: role
      });
      
      if (error) {
        debugLogger.error('[AdminService] Failed to check role:', error);
        return false;
      }
      
      debugLogger.debug('[AdminService] Role checked:', {
        userId,
        role,
        hasRole: data || false
      });
      
      return data || false;
    } catch (error) {
      debugLogger.error('[AdminService] Error in hasRole:', error);
      return false;
    }
  }
  
  /**
   * Limpa o cache de roles
   * @param userId - ID do usuário (opcional, limpa todos se não fornecido)
   */
  static clearCache(userId?: string): void {
    if (userId) {
      this.roleCache.delete(userId);
      debugLogger.debug('[AdminService] Cache cleared for user:', userId);
    } else {
      this.roleCache.clear();
      debugLogger.debug('[AdminService] All cache cleared');
    }
  }
  
  /**
   * Invalida o cache de um usuário quando suas roles mudarem
   * Deve ser chamado após atualizar roles no banco
   */
  static invalidateUserCache(userId: string): void {
    this.clearCache(userId);
  }
  
  /**
   * Verifica se o usuário é moderador
   * @param userId - ID do usuário (opcional, usa auth.uid() se não fornecido)
   * @returns Promise<boolean> - true se é moderador
   */
  static async isModerator(userId?: string): Promise<boolean> {
    try {
      const userIdToCheck = userId || (await supabase.auth.getUser()).data.user?.id;
      
      if (!userIdToCheck) {
        debugLogger.debug('[AdminService] No user ID provided or authenticated');
        return false;
      }
      
      // Verificar cache primeiro
      const cached = this.roleCache.get(userIdToCheck);
      if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
        debugLogger.debug('[AdminService] Using cached moderator status:', {
          userId: userIdToCheck,
          isModerator: cached.isModerator
        });
        return cached.isModerator;
      }
      
      // Se não está no cache, buscar
      const isModerator = await this.hasRole(userIdToCheck, 'moderator');
      
      debugLogger.debug('[AdminService] Moderator status checked:', {
        userId: userIdToCheck,
        isModerator
      });
      
      return isModerator;
    } catch (error) {
      debugLogger.error('[AdminService] Error in isModerator:', error);
      return false;
    }
  }
  
  /**
   * Verifica se o usuário pode acessar o painel admin (admin ou moderador)
   * @param userId - ID do usuário (opcional, usa auth.uid() se não fornecido)
   * @returns Promise<boolean> - true se pode acessar
   */
  static async canAccessAdminPanel(userId?: string): Promise<boolean> {
    const isAdmin = await this.isAdmin(userId);
    if (isAdmin) return true;
    
    return await this.isModerator(userId);
  }
  
  /**
   * Busca roles de um usuário (admin e moderator) em uma única operação
   * Usa cache quando disponível para melhor performance
   * @param userId - ID do usuário
   * @returns Promise com status de admin, moderator e role
   */
  static async getUserRoles(userId: string): Promise<{ 
    isAdmin: boolean; 
    isModerator: boolean;
    role: 'admin' | 'moderator' | 'user';
  }> {
    try {
      const userIdToCheck = userId || (await supabase.auth.getUser()).data.user?.id;
      
      if (!userIdToCheck) {
        return { isAdmin: false, isModerator: false, role: 'user' };
      }
      
      // Verificar cache primeiro
      const cached = this.roleCache.get(userIdToCheck);
      if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
        return { 
          isAdmin: cached.isAdmin, 
          isModerator: cached.isModerator,
          role: cached.isAdmin ? 'admin' : cached.isModerator ? 'moderator' : 'user'
        };
      }
      
      // Buscar role do usuário diretamente da tabela
      const { data: roleData } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userIdToCheck)
        .maybeSingle();
      
      const isAdmin = roleData?.role === 'admin';
      const isModerator = roleData?.role === 'moderator';
      const role = isAdmin ? 'admin' : isModerator ? 'moderator' : 'user';
      
      // Atualizar cache
      this.roleCache.set(userIdToCheck, {
        isAdmin,
        isModerator,
        timestamp: Date.now()
      });
      
      debugLogger.debug('[AdminService] User roles checked (batch)', {
        userId: userIdToCheck,
        isAdmin,
        isModerator,
        role
      });
      
      return { isAdmin, isModerator, role };
    } catch (error) {
      debugLogger.error('[AdminService] Error in getUserRoles:', error);
      return { isAdmin: false, isModerator: false, role: 'user' };
    }
  }
  
  /**
   * Pré-popula cache com roles de múltiplos usuários
   * Útil ao carregar listas de usuários para evitar chamadas RPC individuais
   * @param users - Array de usuários com seus roles
   */
  static preloadRolesCache(users: { id: string; user_role?: string }[]): void {
    users.forEach(user => {
      if (user.user_role) {
        const isAdmin = user.user_role === 'admin';
        const isModerator = user.user_role === 'moderator';
        
        this.roleCache.set(user.id, {
          isAdmin,
          isModerator,
          timestamp: Date.now()
        });
      }
    });
    
    debugLogger.debug('[AdminService] Preloaded roles cache', {
      userCount: users.length
    });
  }
}

// Export singleton instance for convenience
export const adminService = AdminService;
